import { ChangeDetectorRef, Component, OnDestroy, OnInit, NgZone, inject } from '@angular/core';
import { BrMessage } from '@govbr-ds/webcomponents-angular/standalone';
import { GlobalMessageService } from '../../services/global-message.service';
import { CommonModule } from '@angular/common';

interface GlobalMessageItem {
  id: number;
  key: string;
  type: 'info' | 'warning' | 'success' | 'danger';
  message: string;
  ttlMs: number;
  remainingSec: number;
  onDismiss?: () => void;
  timeoutId?: number;
  intervalId?: number;
}

@Component({
  selector: 'app-global-message',
  imports: [BrMessage, CommonModule],
  templateUrl: './global-message.component.html',
  styleUrl: './global-message.component.scss',
  standalone: true,
})
export class GlobalMessageComponent implements OnInit, OnDestroy {
  private service = inject(GlobalMessageService);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  private idSeq = 0;
  messages: GlobalMessageItem[] = [];

  ngOnInit(): void {
    this.service.messageEvent.subscribe({
      next: (msg) => {
        const ttlMs = typeof msg.ttl === 'number' && msg.ttl > 0 ? msg.ttl : 10000; // default 10s
        const key = this.buildMessageKey(msg.type, msg.message);
        this.removeMessageByKey(key);

        const item: GlobalMessageItem = {
          id: ++this.idSeq,
          key,
          type: msg.type,
          message: msg.message,
          ttlMs,
          remainingSec: Math.ceil(ttlMs / 1000),
          onDismiss: msg.onDismiss,
        };

        // Start countdown interval
        item.intervalId = window.setInterval(() => {
          this.zone.run(() => {
            item.remainingSec = Math.max(0, item.remainingSec - 1);
            this.cdr.detectChanges();
          });
        }, 1000);

        // Schedule removal
        item.timeoutId = window.setTimeout(() => {
          this.zone.run(() => this.dismiss(item));
        }, ttlMs);

        this.messages.push(item);
        this.cdr.detectChanges();
      },
    });
  }

  ngOnDestroy(): void {
    // Clear any remaining timers
    this.messages.forEach((m) => this.clearTimers(m));
  }

  onClose(item: GlobalMessageItem) {
    this.zone.run(() => this.dismiss(item));
  }

  private dismiss(item: GlobalMessageItem) {
    item.onDismiss?.();
    this.removeMessageById(item.id);
  }

  private removeMessageById(id: number) {
    const idx = this.messages.findIndex((m) => m.id === id);
    if (idx !== -1) {
      const m = this.messages[idx];
      this.clearTimers(m);
      this.messages.splice(idx, 1);
      this.cdr.detectChanges();
    }
  }

  private removeMessageByKey(key: string) {
    const idx = this.messages.findIndex((m) => m.key === key);
    if (idx !== -1) {
      const m = this.messages[idx];
      this.clearTimers(m);
      this.messages.splice(idx, 1);
      this.cdr.detectChanges();
    }
  }

  private clearTimers(m: GlobalMessageItem) {
    if (m.intervalId) {
      clearInterval(m.intervalId);
      m.intervalId = undefined;
    }
    if (m.timeoutId) {
      clearTimeout(m.timeoutId);
      m.timeoutId = undefined;
    }
  }

  private buildMessageKey(type: GlobalMessageItem['type'], message: string): string {
    return `${type}::${message}`;
  }
}
